import { t } from "@lingui/macro";
import { Button, Tooltip } from "@reactive-resume/ui";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FaRegUser } from "react-icons/fa";
import { MdCleaningServices, MdSend } from "react-icons/md";
import { RiRobot2Line } from "react-icons/ri";
import { RxDoubleArrowRight } from "react-icons/rx";
import ReactMarkdown from "react-markdown";

import useAjaxAiChat, { ChatContent } from "@/client/hooks/use-ajax-ai-chat";
import { useResumeStore } from "@/client/stores/resume";

import { Textarea } from "./textarea";

export const writingChatKeyPrefix = "writingChat";

const AIChatContent = (props: { onClose: () => void }) => {
  const { onClose } = props;

  const resumeData = useResumeStore((state) => state.resume);

  const writingChatKey = `${writingChatKeyPrefix}-${resumeData.id}`;

  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  const [chatContent, setChatContent] = useState<ChatContent[]>([]);

  const [chartInpt, setChartInpt] = useState("");

  const chatMsg = useRef<ChatContent[]>([]);

  const { doAjax, loading: isLoading, data } = useAjaxAiChat();

  const onChat = async () => {
    if (isLoading || !chartInpt) return;
    const message: ChatContent[] = [...chatContent, { role: "user", content: chartInpt }];
    // const content = localStorage.getItem("markdown");
    setChatContent(() => [...message]);
    setChartInpt("");
    await doAjax(resumeData, message);
  };

  useEffect(() => {
    if (!data) return;
    const lastPos = chatContent.length - 1;
    const lastData = chatContent[lastPos];
    const message = [...chatContent];
    if (message[lastPos]?.role === "assistant") {
      message[message.length - 1] = { ...lastData, content: data };
    } else {
      message.push({ role: "assistant", content: data });
    }
    chatMsg.current = message;
    localStorage.setItem(writingChatKey, JSON.stringify(chatMsg.current));
    setChatContent(() => message);
  }, [data]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatContent]);

  useEffect(() => {
    const chatRecord = localStorage.getItem(writingChatKey);
    try {
      if (chatRecord) {
        const data = JSON.parse(chatRecord);
        setChatContent(() => data || []);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);
  return (
    <div className="relative flex h-full flex-1 flex-col justify-between">
      <div className="flex w-full justify-between">
        <Tooltip content={t`SecAIChatClearAll`}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              localStorage.removeItem(writingChatKey);
              setChatContent([]);
            }}
          >
            <MdCleaningServices className="size-5 text-rose-600" />
          </Button>
        </Tooltip>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <RxDoubleArrowRight className="size-5" />
        </Button>
      </div>
      <div ref={chatContainerRef} className="flex h-0 flex-1 flex-col overflow-y-auto">
        {chatContent.map((item, index) => (
          <div
            key={`${index}`}
            className={`flex w-full py-2 ${index === chatContent.length - 1 ? "" : "border-b"}`}
          >
            <div className="mr-3 h-fit">
              {item.role === "user" ? (
                <FaRegUser className="text-lg" />
              ) : (
                <RiRobot2Line className="text-lg" />
              )}
            </div>
            <div
              className={`w-full rounded-sm p-2 text-sm ${item.role === "user" ? "bg-[#ffffff] dark:bg-gray-700" : "bg-[#f0efff] dark:bg-zinc-800"}`}
            >
              <ReactMarkdown className="ReactMarkdown w-full break-words">
                {item.content}
              </ReactMarkdown>
            </div>
          </div>
        ))}
      </div>
      <div className={`flex items-center p-3`}>
        <Textarea
          disabled={isLoading}
          value={chartInpt}
          placeholder={t`SecAIChatInput`}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void onChat();
            }
          }}
          onChange={(e) => {
            setChartInpt(e.target.value);
          }}
        />
        <div
          id="chatBut"
          onClick={() => {
            void onChat();
          }}
        >
          {isLoading ? (
            <Loader2
              className="ml-2 animate-spin text-[#8e47f0]"
              style={{ width: 20, height: 20 }}
            />
          ) : (
            <MdSend className="ml-2 cursor-pointer text-[28px] text-[#8e47f0]" />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIChatContent;
